import React, { useEffect, useRef, useState } from "react";
import { rescueEmoji } from "../utils/emojiRescue";

const DEFAULT_BACKEND =
  (typeof window !== "undefined" &&
    window.__PARTY_BACKEND__) ||
  "https://partybutton-private-a027fa6fce78.herokuapp.com";


function formatDrawingDate(value) {
  if (!value) return "Not yet announced";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not yet announced";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: "America/Los_Angeles",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }
  ).format(date);
}


function formatArv(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "Not yet announced";
  }

  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
    }
  ).format(number);
}


export default function PartyQuestPrize({
  backendBase,
  partyUid,
  quest = null,
  mode = "catalog",
}) {
  const prizeEmojiRootRef = useRef(null);
  const base =
    String(
      backendBase || DEFAULT_BACKEND
    ).replace(/\/+$/, "");

  const [drawing, setDrawing] =
    useState(null);

  const [myEntry, setMyEntry] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [modalOpen, setModalOpen] =
    useState(false);

  const [firstName, setFirstName] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [rulesAccepted, setRulesAccepted] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");


  const request = async (
    path,
    options = {}
  ) => {
    const response =
      await fetch(
        `${base}${path}`,
        {
          ...options,

          cache: "no-store",

          headers: {
            ...(options.body
              ? {
                  "Content-Type":
                    "application/json",
                }
              : {}),

            "X-Party-Uid":
              partyUid,

            ...(options.headers || {}),
          },
        }
      );

    let data = null;

    try {
      data =
        await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const requestError =
        new Error(
          data?.error ||
          `Request failed (${response.status})`
        );

      requestError.data = data;
      requestError.status =
        response.status;

      throw requestError;
    }

    return data;
  };


  const loadDrawing = async () => {
    setLoading(true);
    setError("");

    try {
      const data =
        await request(
          "/api/party-quest-prize"
        );

      setDrawing(
        data?.drawing || null
      );

      setMyEntry(
        data?.myEntry || null
      );
    } catch (requestError) {
      console.warn(
        "Sticker drawing load failed:",
        requestError
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadDrawing();

    // Backend and party UID are stable
    // for this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // PB_PARTY_QUEST_PRIZE_EMOJI_RESCUE_V1
  useEffect(() => {
    const runEmojiRescue = () => {
      if (prizeEmojiRootRef.current) {
        rescueEmoji(
          prizeEmojiRootRef.current
        );
      }
    };

    runEmojiRescue();

    const frame =
      requestAnimationFrame(
        runEmojiRescue
      );

    const timer =
      setTimeout(
        runEmojiRescue,
        120
      );

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [
    loading,
    drawing,
    myEntry,
    modalOpen,
    saving,
    success,
    error,
  ]);


  const completedQuest =
    quest?.progress?.status ===
    "completed";


  const submitEntry = async () => {
    if (!drawing) return;

    if (!firstName.trim()) {
      setError(
        "Please enter your first name."
      );
      return;
    }

    if (!email.trim()) {
      setError(
        "Please enter your email."
      );
      return;
    }

    if (!rulesAccepted) {
      setError(
        "Please agree to the Official Rules."
      );
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const useQuestCompletion =
        completedQuest &&
        quest?.slug;

      const path =
        useQuestCompletion
          ? `/api/quests/${encodeURIComponent(
              quest.slug
            )}/prize-entry`
          : "/api/party-quest-prize/alternate-entry";

      const data =
        await request(
          path,
          {
            method: "POST",

            body:
              JSON.stringify({
                firstName:
                  firstName.trim(),

                email:
                  email.trim(),

                rulesAccepted:
                  true,
              }),
          }
        );

      setMyEntry(
        data?.entry || null
      );

      setSuccess(
        data?.alreadyEntered
          ? "You're already entered in this sticker drawing."
          : "You're entered! Watch your email after the drawing closes."
      );
    } catch (requestError) {
      console.warn(
        "Sticker drawing entry failed:",
        requestError
      );

      setError(
        requestError?.message ||
        "Your entry could not be saved."
      );
    } finally {
      setSaving(false);
    }
  };


  if (
    loading &&
    mode === "catalog"
  ) {
    return null;
  }


  const status =
    drawing?.status || "inactive";

  const drawingOpen =
    status === "open";

  const entryStatus =
    String(
      myEntry?.status || ""
    );


  const entered =
    Boolean(myEntry);


  return (
    <div ref={prizeEmojiRootRef}>
      {mode === "completion" ? (
        <div className="pq-prize-card">
          <div className="pq-prize-icon">
            🎁
          </div>

          <div className="pq-prize-eyebrow">
            PARTY QUEST PRIZE
          </div>

          <h3>
            Win a Party Button Sticker
          </h3>

          {entered ? (
            <>
              <div className="pq-prize-entered">
                ✓ You're entered
              </div>

              <p>
                Your entry for this drawing
                is on file.
              </p>

              {entryStatus ===
              "winner" ? (
                <div className="pq-prize-winner">
                  🎉 You were selected!
                  Check the email address
                  you entered.
                </div>
              ) : null}
            </>
          ) : drawingOpen ? (
            <>
              <p>
                You finished the quest.
                Enter the free sticker
                drawing if you'd like a
                chance to win one.
              </p>

              <button
                type="button"
                className="pq-prize-primary"
                onClick={() =>
                  setModalOpen(true)
                }
              >
                🎟️ Enter Sticker Drawing
              </button>
            </>
          ) : (
            <>
              <p>
                Sticker drawings are coming
                to Party Quest. Official
                details will appear here
                when the next drawing opens.
              </p>

              <button
                type="button"
                className="pq-prize-secondary"
                onClick={() =>
                  setModalOpen(true)
                }
              >
                Official Rules
              </button>
            </>
          )}

          <div className="pq-prize-note">
            No purchase necessary.
            Completing a quest does not
            improve your odds compared with
            the free alternate entry method.
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="pq-prize-catalog"
          onClick={() =>
            setModalOpen(true)
          }
        >
          <span
            className="pq-prize-catalog-icon"
            aria-hidden="true"
          >
            🎁
          </span>

          <span>
            <strong>
              Sticker Drawing
            </strong>

            <small>
              Official Rules & free
              alternate entry
            </small>
          </span>

          <span
            className="pq-prize-arrow"
            aria-hidden="true"
          >
            ›
          </span>
        </button>
      )}


      {modalOpen ? (
        <div
          className="pq-prize-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Party Quest Sticker Drawing Official Rules"
        >
          <div className="pq-prize-modal">
            <div className="pq-prize-modal-header">
              <div>
                <div className="pq-prize-modal-eyebrow">
                  PARTY QUEST
                </div>

                <h2>
                  🎁 Sticker Drawing
                </h2>
              </div>

              <button
                type="button"
                className="pq-prize-close"
                onClick={() => {
                  setModalOpen(false);
                  setError("");
                  setSuccess("");
                }}
                aria-label="Close sticker drawing"
              >
                ×
              </button>
            </div>


            <div className="pq-rules-box">
              <h3>
                Official Rules
              </h3>

              {!drawing?.configured ? (
                <div className="pq-rules-draft">
                  The next drawing is not
                  active yet. Entry dates
                  and prize value will be
                  published before entries
                  are accepted.
                </div>
              ) : null}

              <p>
                <strong>
                  Sponsor:
                </strong>{" "}
                {drawing?.sponsor ||
                  "The Party Button LLC"}
              </p>

              <p>
                <strong>
                  Eligibility:
                </strong>{" "}
                {drawing?.eligibility ||
                  "Eligibility will be announced before the drawing opens."}
              </p>

              <p>
                <strong>
                  Entry period:
                </strong>{" "}
                {formatDrawingDate(
                  drawing?.startsAt
                )}{" "}
                through{" "}
                {formatDrawingDate(
                  drawing?.endsAt
                )}.
              </p>

              <p>
                <strong>
                  Prize:
                </strong>{" "}
                {drawing?.prizeDescription ||
                  "One The Party Button sticker"}
                . Approximate retail value:{" "}
                {formatArv(
                  drawing?.prizeArv
                )}.
              </p>

              <p>
                <strong>
                  How to enter:
                </strong>{" "}
                An eligible person may
                submit one entry during the
                entry period. Party Quest
                completers may use the entry
                form shown after completion.
                A Party Quest completion is
                not required: the free
                alternate entry form on this
                screen may also be used.
              </p>

              <p>
                {drawing?.entryLimit ||
                  "Limit one entry per person during the drawing period."}
              </p>

              <p>
                <strong>
                  No purchase necessary.
                </strong>{" "}
                A purchase will not increase
                the odds of winning. Quest
                completion and alternate
                entries receive the same
                chance of selection.
              </p>

              <p>
                <strong>
                  Winner selection:
                </strong>{" "}
                {drawing?.winnerNotice ||
                  "One eligible entry will be selected at random after the entry period closes."}
              </p>

              <p>
                {drawing?.oddsNotice ||
                  "Odds depend on the number of eligible entries received."}
              </p>

              <p>
                <strong>
                  Privacy:
                </strong>{" "}
                {drawing?.privacyNotice ||
                  "Entry information is used to administer the drawing."}
              </p>

              <p>
                {drawing?.voidNotice ||
                  "Void where prohibited."}
              </p>

              <p className="pq-apple-disclaimer">
                {drawing?.appleDisclaimer ||
                  "Apple Inc. is not a sponsor of, affiliated with, or involved in this promotion in any manner."}
              </p>
            </div>


            {entered ? (
              <div className="pq-entry-confirmation">
                <div>
                  ✓ Entry received
                </div>

                <strong>
                  {myEntry.firstName}
                </strong>

                <small>
                  {myEntry.email}
                </small>

                {entryStatus ===
                "winner" ? (
                  <p>
                    🎉 Your entry was
                    selected as the winner.
                  </p>
                ) : (
                  <p>
                    We'll use this email only
                    to administer this
                    drawing and contact a
                    potential winner.
                  </p>
                )}
              </div>
            ) : drawingOpen ? (
              <div className="pq-entry-form">
                <h3>
                  {completedQuest
                    ? "Enter the sticker drawing"
                    : "Free alternate entry"}
                </h3>

                {!completedQuest ? (
                  <p className="pq-entry-help">
                    No quest completion or
                    purchase is required.
                  </p>
                ) : null}

                <label>
                  First name
                  <input
                    type="text"
                    value={firstName}
                    maxLength={80}
                    autoComplete="given-name"
                    onChange={(event) =>
                      setFirstName(
                        event.target.value
                      )
                    }
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    value={email}
                    maxLength={254}
                    autoComplete="email"
                    autoCapitalize="none"
                    onChange={(event) =>
                      setEmail(
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="pq-rules-agree">
                  <input
                    type="checkbox"
                    checked={rulesAccepted}
                    onChange={(event) =>
                      setRulesAccepted(
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    I meet the eligibility
                    requirements and agree
                    to the Official Rules.
                  </span>
                </label>

                {error ? (
                  <div className="pq-prize-error">
                    {error}
                  </div>
                ) : null}

                {success ? (
                  <div className="pq-prize-success">
                    {success}
                  </div>
                ) : null}

                <button
                  type="button"
                  className="pq-prize-primary"
                  disabled={
                    saving ||
                    !rulesAccepted
                  }
                  onClick={submitEntry}
                >
                  {saving
                    ? "Entering…"
                    : completedQuest
                    ? "Enter Sticker Drawing"
                    : "Submit Free Entry"}
                </button>
              </div>
            ) : (
              <div className="pq-entry-not-open">
                {status === "upcoming"
                  ? "The next sticker drawing has not opened yet."
                  : status === "closed"
                  ? "This sticker drawing has closed."
                  : "The sticker drawing is not accepting entries yet."}
              </div>
            )}


            <div className="pq-prize-contact">
              Questions about the drawing?{" "}
              {drawing?.contactEmail ||
                "support@the-party-button.com"}
            </div>
          </div>
        </div>
      ) : null}


      <style>{`
        .pq-prize-card{
          margin-top:18px;
          padding:22px 17px;
          text-align:center;
          border-radius:22px;
          border:
            1px solid rgba(249,168,212,.24);
          background:
            linear-gradient(
              135deg,
              rgba(236,72,153,.18),
              rgba(59,130,246,.16)
            );
        }

        .pq-prize-icon{
          font-size:43px;
        }

        .pq-prize-eyebrow,
        .pq-prize-modal-eyebrow{
          margin-top:5px;
          color:#93c5fd;
          font-size:11px;
          font-weight:950;
          letter-spacing:.1em;
        }

        .pq-prize-card h3{
          margin:6px 0 7px;
          font-size:21px;
        }

        .pq-prize-card p{
          margin:7px auto 13px;
          max-width:440px;
          color:#dbeafe;
          font-size:14px;
          line-height:1.45;
        }

        .pq-prize-primary{
          width:100%;
          min-height:48px;
          border:0;
          border-radius:15px;
          color:#fff;
          font-size:15px;
          font-weight:950;
          background:
            linear-gradient(
              90deg,
              #ec4899,
              #2563eb
            );
        }

        .pq-prize-secondary{
          width:100%;
          min-height:44px;
          border-radius:14px;
          border:
            1px solid rgba(255,255,255,.18);
          background:
            rgba(255,255,255,.08);
          color:#fff;
          font-weight:900;
        }

        .pq-prize-note{
          margin-top:11px;
          color:#94a3b8;
          font-size:10px;
          line-height:1.4;
        }

        .pq-prize-entered{
          display:inline-block;
          margin-top:6px;
          padding:7px 11px;
          border-radius:999px;
          color:#99f6e4;
          background:
            rgba(20,184,166,.14);
          font-size:13px;
          font-weight:950;
        }

        .pq-prize-winner{
          margin-top:12px;
          padding:11px;
          border-radius:14px;
          background:
            rgba(251,191,36,.16);
          color:#fde68a;
          font-weight:900;
        }

        .pq-prize-catalog{
          width:100%;
          display:flex;
          align-items:center;
          gap:11px;
          margin-bottom:14px;
          padding:13px 14px;
          text-align:left;
          border-radius:17px;
          border:
            1px solid rgba(249,168,212,.18);
          color:#fff;
          background:
            rgba(255,255,255,.06);
        }

        .pq-prize-catalog-icon{
          font-size:25px;
          flex:0 0 auto;
        }

        .pq-prize-catalog > span:nth-child(2){
          flex:1;
          min-width:0;
          display:flex;
          flex-direction:column;
        }

        .pq-prize-catalog strong{
          font-size:14px;
        }

        .pq-prize-catalog small{
          margin-top:2px;
          color:#c7d2fe;
          font-size:11px;
          font-weight:800;
        }

        .pq-prize-arrow{
          font-size:27px;
          opacity:.6;
        }

        .pq-prize-modal-backdrop{
          position:fixed;
          inset:0;
          z-index:10050;
          overflow-y:auto;
          -webkit-overflow-scrolling:touch;
          padding:
            calc(14px + env(safe-area-inset-top))
            12px
            calc(14px + env(safe-area-inset-bottom));
          background:
            rgba(2,6,23,.94);
        }

        .pq-prize-modal{
          width:min(100%,680px);
          margin:0 auto;
          padding:17px;
          box-sizing:border-box;
          border-radius:22px;
          color:#fff;
          background:#101827;
          border:
            1px solid rgba(255,255,255,.11);
        }

        .pq-prize-modal-header{
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:14px;
          margin-bottom:14px;
        }

        .pq-prize-modal-header h2{
          margin:4px 0 0;
          font-size:25px;
        }

        .pq-prize-close{
          width:40px;
          height:40px;
          flex:0 0 40px;
          border-radius:50%;
          border:
            1px solid rgba(255,255,255,.16);
          background:
            rgba(255,255,255,.07);
          color:#fff;
          font-size:23px;
        }

        .pq-rules-box{
          padding:15px;
          border-radius:17px;
          color:#dbeafe;
          background:
            rgba(255,255,255,.055);
          border:
            1px solid rgba(255,255,255,.08);
          font-size:12px;
          line-height:1.5;
        }

        .pq-rules-box h3{
          margin:0 0 10px;
          color:#fff;
          font-size:17px;
        }

        .pq-rules-box p{
          margin:8px 0;
        }

        .pq-rules-draft{
          margin-bottom:12px;
          padding:10px;
          border-radius:12px;
          color:#fde68a;
          background:
            rgba(251,191,36,.12);
          border:
            1px solid rgba(251,191,36,.2);
          font-weight:850;
        }

        .pq-apple-disclaimer{
          padding-top:9px;
          border-top:
            1px solid rgba(255,255,255,.09);
          font-weight:850;
        }

        .pq-entry-form,
        .pq-entry-confirmation{
          margin-top:14px;
          padding:16px;
          border-radius:17px;
          background:
            rgba(59,130,246,.1);
          border:
            1px solid rgba(96,165,250,.18);
        }

        .pq-entry-form h3{
          margin:0 0 4px;
        }

        .pq-entry-help{
          margin:0 0 13px;
          color:#bfdbfe;
          font-size:12px;
        }

        .pq-entry-form label:not(.pq-rules-agree){
          display:block;
          margin-top:12px;
          color:#dbeafe;
          font-size:12px;
          font-weight:900;
        }

        .pq-entry-form input[type="text"],
        .pq-entry-form input[type="email"]{
          width:100%;
          min-height:44px;
          box-sizing:border-box;
          margin-top:5px;
          padding:10px 12px;
          border-radius:12px;
          border:
            1px solid rgba(255,255,255,.15);
          background:
            rgba(255,255,255,.08);
          color:#fff;
          font-size:16px;
        }

        .pq-rules-agree{
          display:flex;
          align-items:flex-start;
          gap:9px;
          margin:14px 0;
          color:#c7d2fe;
          font-size:11px;
          line-height:1.4;
        }

        .pq-rules-agree input{
          margin-top:2px;
        }

        .pq-prize-error,
        .pq-prize-success{
          margin-bottom:11px;
          padding:10px;
          border-radius:12px;
          font-size:12px;
          font-weight:850;
        }

        .pq-prize-error{
          color:#fecaca;
          background:
            rgba(239,68,68,.13);
        }

        .pq-prize-success{
          color:#ccfbf1;
          background:
            rgba(20,184,166,.13);
        }

        .pq-entry-confirmation{
          text-align:center;
        }

        .pq-entry-confirmation > div{
          color:#99f6e4;
          font-weight:950;
        }

        .pq-entry-confirmation strong,
        .pq-entry-confirmation small{
          display:block;
          margin-top:5px;
        }

        .pq-entry-confirmation small{
          color:#bfdbfe;
        }

        .pq-entry-confirmation p{
          color:#c7d2fe;
          font-size:12px;
          line-height:1.4;
        }

        .pq-entry-not-open{
          margin-top:14px;
          padding:15px;
          text-align:center;
          border-radius:15px;
          color:#c7d2fe;
          background:
            rgba(255,255,255,.055);
        }

        .pq-prize-contact{
          padding:15px 4px 2px;
          color:#94a3b8;
          text-align:center;
          font-size:10px;
        }

        button:disabled{
          opacity:.5;
          cursor:not-allowed;
        }
      `}</style>
    </div>
  );
}
